import { useQuery } from '@tanstack/react-query';
import { useDomainMutation } from '@pps/mutations';
import { qk } from '@pps/query-keys';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useScopedClubIds } from '@/hooks/useScopedClubIds';
import i18n from '@/i18n';

export interface DailyFollowUp {
  id: string;
  lead_id: string;
  product_type: string;
  estimated_value: number;
  next_follow_up_at: string;
  lead?: { name: string } | null;
}

export interface DailyAiTask {
  id: string;
  lead_id: string;
  description: string;
  created_at: string;
  metadata: Record<string, unknown>;
  lead_name?: string;
}

export function useDailyActivities() {
  const { club, viewScope } = useAuth();
  const { scopedClubIds } = useScopedClubIds();
  const scopedClubIdSet = new Set(scopedClubIds);

  const followUpsQuery = useQuery({
    queryKey: ['daily-followups', club?.id, viewScope],
    queryFn: async () => {
      if (!club || scopedClubIds.length === 0) return [];
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('opportunities')
        .select('id, lead_id, product_type, estimated_value, next_follow_up_at, lead:leads(name)')
        .in('club_id', scopedClubIds)
        .eq('status', 'open')
        .not('next_follow_up_at', 'is', null)
        .lte('next_follow_up_at', now)
        .order('next_follow_up_at', { ascending: true })
        .limit(20);
      if (error) throw error;
      return ((data ?? []) as DailyFollowUp[]);
    },
    enabled: !!club,
    refetchInterval: 60_000,
  });

  const aiTasksQuery = useQuery({
    queryKey: ['daily-ai-tasks', club?.id, viewScope],
    queryFn: async () => {
      if (!club || scopedClubIds.length === 0) return [];
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('lead_activities')
        .select('id, lead_id, description, created_at, metadata, lead:leads!inner(name, club_id)')
        .eq('type', 'task')
        .gte('created_at', weekAgo)
        .order('created_at', { ascending: false })
        .limit(30);
      if (error) throw error;
      // Filter client-side por scopedClubIds (rede ou clube único) +
      // ai_generated + não-completas.
      type LeadActivityRow = {
        id: string;
        lead_id: string;
        description: string;
        created_at: string;
        metadata: Record<string, unknown> | null;
        lead: { name: string; club_id: string } | null;
      };
      const rows = (data ?? []) as LeadActivityRow[];
      return rows
        .filter((a) =>
          a.lead?.club_id && scopedClubIdSet.has(a.lead.club_id) &&
          a.metadata?.ai_generated === true &&
          !a.metadata?.completed
        )
        .map<DailyAiTask>((a) => ({
          id: a.id,
          lead_id: a.lead_id,
          description: a.description,
          created_at: a.created_at,
          metadata: a.metadata ?? {},
          lead_name: a.lead?.name,
        }));
    },
    enabled: !!club,
    refetchInterval: 60_000,
  });

  const completeFollowUp = useDomainMutation({
    mutationFn: async (oppId: string) => {
      const { error } = await supabase
        .from('opportunities')
        .update({ next_follow_up_at: null, updated_at: new Date().toISOString() })
        .eq('id', oppId);
      if (error) throw error;
    },
    invalidates: [['daily-followups'], qk.sales.opportunities.all],
    toast: { error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'complete_followup' },
  });

  const completeAiTask = useDomainMutation({
    mutationFn: async ({ activityId, currentMetadata }: { activityId: string; currentMetadata: Record<string, unknown> }) => {
      const { error } = await supabase
        .from('lead_activities')
        .update({ metadata: { ...currentMetadata, completed: true } })
        .eq('id', activityId);
      if (error) throw error;
    },
    invalidates: ['daily-ai-tasks'],
    toast: { error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'complete_ai_task' },
  });

  const followUps = followUpsQuery.data ?? [];
  const aiTasks = aiTasksQuery.data ?? [];

  // Sort AI tasks by priority
  const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
  const sortedAiTasks = [...aiTasks].sort((a, b) => {
    const pa = priorityOrder[(a.metadata?.priority as string) || 'low'] ?? 2;
    const pb = priorityOrder[(b.metadata?.priority as string) || 'low'] ?? 2;
    if (pa !== pb) return pa - pb;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  return {
    followUps,
    aiTasks: sortedAiTasks,
    totalCount: followUps.length + sortedAiTasks.length,
    isLoading: followUpsQuery.isLoading || aiTasksQuery.isLoading,
    completeFollowUp,
    completeAiTask,
  };
}
